import { FETCH_LOCATIONS } from '@/actions';
import { locationsStateAtom } from '@/atoms';
import { NavLink, SearchBox, SortField } from '@/components/controls';
import { useAuth, useDocumentTitle, useGroups, useOptions } from '@/hooks';
import {
  downloadCSV,
  downloadGeoJSON,
  getFilenameForDownload,
  getLocations,
  startCase,
} from '@/utils';
import { locationTypes } from '@/utils/config';
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { format } from 'date-fns';
import { dequal } from 'dequal';
import { useAtom } from 'jotai';
import _ from 'lodash';
import { GeoJSON, WKT } from 'ol/format';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Outlet, useParams } from 'react-router-dom';

const locationHeaders = [
  { label: 'Code', key: 'code' },
  { label: 'Name', key: 'name' },
  { label: 'Type', key: 'type' },
  { label: 'Subtype', key: 'subtype' },
  { label: 'Start Time', key: 'startTime' },
  { label: 'End Time', key: 'endTime' },
  { label: 'Boundary WKT', key: 'boundaryWkt' },
];

async function getLocationsAndHeaders(type) {
  const data = await getLocations(type);

  const groupTypes = Array.from(
    new Set(
      [].concat(...data.map((record) => Object.keys(record.groups || {}))),
    ),
  );

  const locations = data.map(
    ({ areas, boundary, startTime, endTime, ...vehicle }) => {
      const geometry = new GeoJSON().readGeometry(boundary, {
        // featureProjection: 'EPSG:3857',
      });

      const boundaryWkt = new WKT().writeGeometry(geometry, {
        dataProjection: 'EPSG:4326',
        rightHanded: true,
      });

      return {
        ...vehicle,
        boundaryWkt,
        startTime: startTime
          ? format(new Date(startTime), 'dd/MM/yyyy HH:mm:ss')
          : '',
        endTime: endTime
          ? format(new Date(endTime), 'dd/MM/yyyy HH:mm:ss')
          : '',
        ...(areas || []).reduce((accumulator, area) => {
          if (area.name !== undefined) {
            accumulator[area.type] = area.name;
          }

          return accumulator;
        }, {}),
      };
    },
  );

  return {
    locations,
    headers: [
      ...locationHeaders,
      ...groupTypes.map((type) => ({
        label: startCase(type),
        key: type,
        type: 'text',
      })),
    ],
  };
}

export function Locations() {
  const { id, locationType } = useParams();
  useDocumentTitle(`IR3 | ${locationTypes[locationType]?.header}`);
  const [{ searchText, sortBy, sortDesc, filter, showSettings }, setState] =
    useAtom(locationsStateAtom);
  const dispatch = useDispatch();
  const locations = useSelector((state) => state.locations.locations, dequal);
  const { data: groups } = useGroups();
  const { data: typeOptions } = useOptions('groupType');
  const parentRef = useRef();
  const auth = useAuth();
  const canCreate = auth.isAuthorised('locations', true);

  const sortOptions = [
    { label: 'Name', value: 'name' },
    { label: 'Code', value: 'code' },
  ];

  useEffect(() => {
    dispatch({
      type: FETCH_LOCATIONS,
      payload: locationTypes[locationType].value,
    });
  }, [dispatch, locationType]);

  const filteredList = useMemo(() => {
    return _.orderBy(
      locations
        .filter(
          (item) =>
            !filter.ancestor ||
            item.groupAncestorCodes.includes(filter.ancestor.value),
        )
        .filter(
          (item) =>
            item.searchString.indexOf(searchText.toLowerCase()) > -1 ||
            searchText === '',
        ),
      [sortBy],
      [sortDesc ? 'desc' : 'asc'],
    );
  }, [filter, locations, searchText, sortBy, sortDesc]);
  const ancestors = useMemo(
    () =>
      groups
        .map((group) => ({
          label: group.name,
          value: group.code,
          type: typeOptions[group.type] ?? '',
        }))
        .sort(
          (a, b) =>
            a.type.localeCompare(b.type) || a.label.localeCompare(b.label),
        ),
    [groups, typeOptions],
  );

  const rowVirtualizer = useVirtualizer({
    count: filteredList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });

  function handleSearchChange(event) {
    setState((state) => ({
      ...state,
      searchText: event.target.value,
    }));
  }

  function handleSettingsToggle() {
    setState((state) => ({
      ...state,
      showSettings: !state.showSettings,
    }));
  }

  function handleSortByChange(event) {
    setState((state) => ({
      ...state,
      sortBy: event.target.value,
    }));
  }

  function handleSortToggle() {
    setState((state) => ({
      ...state,
      sortDesc: !state.sortDesc,
    }));
  }

  function handleAncestorFilterChange(event, option) {
    setState((state) => ({
      ...state,
      filter: {
        ...state.filter,
        ancestor: option,
      },
    }));
  }

  function getFilteredDataForDownload(data) {
    const filteredData = data.filter((o1) =>
      filteredList.some((o2) => o1.code === o2.code),
    );

    return filteredData;
  }

  async function handleCsvClick() {
    const data = await getLocationsAndHeaders(
      locationTypes[locationType].value,
    );
    const filteredData = getFilteredDataForDownload(data.vehicles);
    const filename = getFilenameForDownload(`locations-${locationType}`, 'csv');
    downloadCSV(filteredData, filename, data.headers);
  }

  async function handleGeoJsonClick() {
    const data = await getLocations(locationTypes[locationType].value);
    const filteredData = getFilteredDataForDownload(data);
    const filename = getFilenameForDownload(
      `locations-${locationType}`,
      'json',
    );
    downloadGeoJSON(filteredData, filename);
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)' }}>
      <Stack
        sx={{
          width: 280,
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
          height: 1,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ p: 1, alignItems: 'center' }}
          >
            <SearchBox
              value={searchText}
              onChange={handleSearchChange}
              count={`${filteredList.length}/${locations.length}`}
              sx={{ flexGrow: 1 }}
            />
            <Tooltip title={showSettings ? 'Hide settings' : 'Show settings'}>
              <IconButton onClick={handleSettingsToggle} size="small">
                <SettingsIcon
                  fontSize="inherit"
                  color={showSettings ? 'primary' : 'inherit'}
                />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Tooltip title="Add new">
                <IconButton component={Link} to="new" size="small">
                  <Avatar
                    sx={{
                      color: 'secondary.contrastText',
                      backgroundColor: 'secondary.main',
                      width: 24,
                      height: 24,
                      fontSize: 16,
                    }}
                  >
                    <AddIcon fontSize="inherit" />
                  </Avatar>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Collapse in={showSettings} unmountOnExit>
            <Stack sx={{ p: 1 }} spacing={1}>
              <SortField
                sortBy={sortBy}
                onSortByChange={handleSortByChange}
                sortDesc={sortDesc}
                onSortDescToggle={handleSortToggle}
                options={sortOptions}
              />
            </Stack>
            <Stack sx={{ px: 1, pb: 1 }} spacing={1}>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Filters
                </Typography>
              </Divider>
              <Autocomplete
                value={filter.ancestor}
                onChange={handleAncestorFilterChange}
                options={ancestors}
                groupBy={(option) => option.type}
                getOptionLabel={(option) => option?.label ?? option}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Group | Area" />
                )}
              />
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  Export
                </Typography>
              </Divider>
              <Stack
                direction="row"
                // sx={{ p: 1 }}
                spacing={1}
                justifyContent="center"
              >
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  disableElevation
                  onClick={handleCsvClick}
                >
                  CSV
                </Button>
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  disableElevation
                  onClick={handleGeoJsonClick}
                >
                  GeoJSON
                </Button>
              </Stack>
            </Stack>
          </Collapse>
        </Box>
        <Divider />
        <Box ref={parentRef} sx={{ overflow: 'auto', height: 1 }}>
          <Box
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map(({ index, size, start }) => {
              const item = filteredList[index];

              return (
                <ListItemButton
                  dense
                  key={index}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${size}px`,
                    transform: `translateY(${start}px)`,
                  }}
                  component={NavLink}
                  to={`/locations/${locationType}/${encodeURIComponent(
                    item.code,
                  )}`}
                  selected={id === item.code}
                >
                  <ListItemAvatar>
                    <Avatar src={item.picture}>
                      <SvgIcon>
                        <path d={locationTypes[locationType].d} />
                      </SvgIcon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={item.code}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        </Box>
      </Stack>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
